import { refreshDatabase } from 'src/actions/databases/action-creators/refreshDatabase';
import { refreshDatabases } from 'src/actions/databases/action-creators/refreshDatabases';
import { fetchModelMetadata } from 'src/actions/projects/utils/fetchModelMetadata';
import { projectModelSelector } from 'src/selectors/projectModel';
import { Connection } from 'src/store/connections/types';
import { ProjectModel } from 'src/store/state/projects';
import { AppDispatch } from 'src/store/store';
import { ReduxStateGetter } from 'src/store/types';

/**
 * Refreshes a project model from its source database.
 * This fetches the latest model data (classes, properties, rules, metadata)
 * and updates the project state.
 */
export const refreshProjectModel =
  ({
    projectId,
    modelId,
    connection,
  }: {
    projectId: string;
    modelId: string;
    connection: Connection;
  }) =>
  async (dispatch: AppDispatch, getState: ReduxStateGetter) => {
    const projectModel = projectModelSelector(projectId, modelId)(getState());
    if (!projectModel) {
      throw new Error('Project model not found');
    }

    const { databaseId, importedModelName } = projectModel;

    if (!databaseId || !importedModelName) {
      throw new Error(
        'Cannot refresh model: missing database ID or original model name'
      );
    }

    // Connection is already established by the hook before calling this action
    await dispatch(refreshDatabases());
    await dispatch(refreshDatabase(databaseId));

    // Get fresh database from state after refresh
    const { databases } = getState();
    const database = databases.databasesById?.[databaseId];

    if (!database) {
      throw new Error('Database not found');
    }

    const { namedGraphsByModel = {} } = database;
    const namedGraphs = namedGraphsByModel[importedModelName];

    if (!namedGraphs) {
      throw new Error(
        `Model "${importedModelName}" no longer exists in database "${databaseId}"`
      );
    }

    const {
      classes,
      colorIdsByIri,
      descriptionsByIri,
      geoDataByIri,
      labelsByIri,
      properties,
      rules,
      voiceboxInstructions,
    } = await fetchModelMetadata({
      connection,
      databaseId,
      databaseNamespaces: database.namespaces || {},
      modelId,
      modelName: importedModelName,
      namedGraphs,
      projectId,
    });

    if (!classes || !properties) {
      throw new Error('Failed to fetch model data for refresh');
    }

    const refreshedModel: ProjectModel = {
      ...projectModel,
      classes,
      properties,
      colorIdsByIri: {
        ...projectModel.colorIdsByIri,
        ...colorIdsByIri,
      },
      descriptionsByIri: {
        ...projectModel.descriptionsByIri,
        ...descriptionsByIri,
      },
      geoDataByIri: {
        ...projectModel.geoDataByIri,
        ...geoDataByIri,
      },
      labelsByIri: {
        ...projectModel.labelsByIri,
        ...labelsByIri,
      },
      rules,
      voiceboxInstructions,
      dateLastUpdated: Date.now(),
    };

    return {
      refreshedModel,
      database,
    };
  };
